// Node calculations and coordinate lookups — the single place the client and
// server reach for "which node is this", "where is it", and "how far apart are
// two nodes". Graph *construction* (assigning the ids) lives in
// `coords/board.ts` (`buildBoard`); everything that *queries* an already-built
// `BoardGraph` lives here, so there is one source of truth for node math.
//
// A node's canonical identity is its `coord` (corner coordinate); the `id` is a
// dense integer derived from `coord` for array/wire use (see AGENTS.md). Both
// are deterministic from `radius`, so client and server agree on every result.
// All pure — no Colyseus runtime.
import { cubeKey, cubeRingIndex, rotateCW } from "../coords/cube.js";
// --- id ↔ coordinate ↔ node -----------------------------------------------
/**
 * id → node. The `nodes` array IS the table (the id is its dense index), so this
 * is an O(1) lookup; returns `undefined` for an out-of-range id.
 */
export function nodeById(graph, id) {
    return graph.nodes[id];
}
/** id → coordinate: the node's corner coordinate, or `undefined` for a bad id. */
export function coordOf(graph, id) {
    return graph.nodes[id]?.coord;
}
/**
 * coordinate → id: the id of the node at corner coordinate `coord`, or
 * `undefined` if that corner isn't on the board. Uses the reverse index
 * `buildBoard` hands back (`idByCoord`), so it's an O(1) lookup with no rebuild —
 * the inverse of reading `graph.nodes[id].coord`.
 */
export function idAt(graph, coord) {
    return graph.idByCoord.get(cubeKey(coord));
}
/**
 * True when `coord` lands on a node (a honeycomb corner) rather than a hex
 * centre or an edge midpoint. In the corner-coordinate frame the three
 * components of a corner are all ≡ the same nonzero value (mod 3) — the two
 * triangle orientations — while a hex centre is all ≡ 0, so `(0,0,0)` (the board
 * centre) is never a node. This is a geometric test only; use `idAt` to also
 * check the coordinate is within the board.
 */
export function isNodeCoord(coord) {
    const m = (n) => ((n % 3) + 3) % 3;
    const a = m(coord.q);
    return a !== 0 && a === m(coord.r) && a === m(coord.s);
}
// Neighbour offsets for a class-A corner (components ≡ 1 mod 3). A class-B corner
// (≡ 2) uses the negation of these — verified against the built graph. A corner
// has at most three neighbours; some offsets land off-board near the rim.
const NEIGHBOR_OFFSETS = [
    { q: 1, r: 1, s: -2 },
    { q: 1, r: -2, s: 1 },
    { q: -2, r: 1, s: 1 },
];
/**
 * The (≤3) corner coordinates adjacent to `coord` in the honeycomb — pure offset
 * arithmetic, no graph needed. Results near the rim may be off-board: map through
 * `idAt` and drop `undefined` to keep only the real neighbours (which then match
 * `graph.adjacency[idAt(coord)]`). Assumes `coord` is a node coordinate.
 */
export function cornerNeighbors(coord) {
    // The two corner classes have opposite-signed offsets; pick the sign by class.
    const sign = ((coord.q % 3) + 3) % 3 === 1 ? 1 : -1;
    return NEIGHBOR_OFFSETS.map((o) => ({
        q: coord.q + sign * o.q,
        r: coord.r + sign * o.r,
        s: coord.s + sign * o.s,
    }));
}
/**
 * Rotate a corner coordinate `steps` × 60° about the board centre (positive =
 * the `rotateCW` direction). Rotation is linear and a corner coordinate is a
 * linear combination of its hexes, so this is just `rotateCW` applied to the
 * coordinate — the result is another valid corner. Use it to map one player's
 * wedge onto another's (the board's 6-fold symmetry), then `idAt` for the id.
 */
export function rotateCorner(coord, steps) {
    let out = coord;
    const n = ((steps % 6) + 6) % 6;
    for (let i = 0; i < n; i++)
        out = rotateCW(out);
    return out;
}
// --- Ring distance & adjacency --------------------------------------------
/** Ring distance of the node from the board centre = its closest hex. */
export function nodeRingIndex(node) {
    return Math.min(...node.hexes.map(cubeRingIndex));
}
/** True when `to` is exactly one edge away from `from`. */
export function areAdjacent(graph, from, to) {
    return graph.adjacency[from]?.includes(to) ?? false;
}
/**
 * Canonical key for the undirected edge between two node ids — `"lo-hi"`,
 * independent of argument order. Used to address edges in override sets (severed
 * paths / chasms) without mutating the derived graph (CLAUDE.md).
 */
export function edgeKey(a, b) {
    return a < b ? `${a}-${b}` : `${b}-${a}`;
}
/**
 * The one traversal all reach queries below share: breadth-first distances from
 * `starts`, out to `maxSteps` PAID edges.
 *
 * `freeEdges` make it a 0-1 BFS. Before each paid step the current layer is
 * flooded across its zero-cost edges, so everything a chain of them reaches is
 * recorded at the SAME distance and expands alongside the layer that found it.
 * With no `freeEdges` this is the plain BFS it has always been.
 *
 * `into` is Aim Range's relaxation: `blocked` nodes may be ENTERED as a route's
 * final step (recorded in the result) but never expand the frontier, so nothing
 * beyond a blocker is reachable through it. Without it a blocked node is not
 * reached at all.
 */
function reachFrom(graph, starts, maxSteps, blocked, blockedEdges, freeEdges, into) {
    const dist = new Map();
    let frontier = [];
    for (const s of starts) {
        if (!dist.has(s)) {
            dist.set(s, 0);
            frontier.push(s);
        }
    }
    // Flood `layer` across zero-cost edges, recording arrivals at `step`, and
    // return the layer the next PAID step expands from. Grows in place, so a
    // chain of free edges is followed to its end.
    const flood = (layer, step) => {
        if (!freeEdges || freeEdges.size === 0)
            return layer;
        const out = layer.slice();
        for (let i = 0; i < out.length; i++) {
            const id = out[i];
            for (const nb of graph.adjacency[id] ?? []) {
                if (dist.has(nb) || !freeEdges.has(edgeKey(id, nb)))
                    continue;
                if (!into && blocked?.has(nb))
                    continue;
                if (blockedEdges?.has(edgeKey(id, nb)))
                    continue; // severed path
                dist.set(nb, step);
                if (!into || !blocked?.has(nb))
                    out.push(nb); // enterable, not traversable
            }
        }
        return out;
    };
    frontier = flood(frontier, 0);
    for (let step = 1; step <= maxSteps && frontier.length > 0; step++) {
        const next = [];
        for (const id of frontier) {
            for (const nb of graph.adjacency[id] ?? []) {
                if (dist.has(nb))
                    continue;
                if (!into && blocked?.has(nb))
                    continue;
                if (blockedEdges?.has(edgeKey(id, nb)))
                    continue; // severed path
                dist.set(nb, step);
                if (!into || !blocked?.has(nb))
                    next.push(nb); // enterable, not traversable
            }
        }
        frontier = flood(next, step);
    }
    return dist;
}
/**
 * Breadth-first node distances from `start`, out to `maxSteps` edges, honouring
 * `blocked` nodes and `blockedEdges` (severed paths). Returns a map of reachable
 * node id → step distance, including `start` at 0. This is the canonical board
 * traversal — build Range and Line of Sight are both "is the target in this
 * map?" queries.
 *
 * `freeEdges` cost nothing to cross (see `NodeReachOptions`).
 */
export function nodesWithin(graph, start, maxSteps, blocked, blockedEdges, freeEdges) {
    return reachFrom(graph, [start], maxSteps, blocked, blockedEdges, freeEdges, false);
}
/**
 * Like `nodesWithin`, but seeded from MANY sources at once: returns each reached
 * node's distance to the NEAREST of `starts` (each itself at 0), out to
 * `maxSteps`, honouring the same `blocked` / `blockedEdges` / `freeEdges`. One
 * BFS instead of one-per-source — e.g. every node within build Range of *any* of
 * a player's buildings.
 */
export function nodesWithinAny(graph, starts, maxSteps, blocked, blockedEdges, freeEdges) {
    return reachFrom(graph, starts, maxSteps, blocked, blockedEdges, freeEdges, false);
}
/**
 * Like `nodesWithin`, but `blocked` nodes may be ENTERED as a path's final step
 * — they just can't be traversed THROUGH (they never expand the frontier).
 * This is Aim Range: a Barracks may aim AT a hostile blocker (a Barracks,
 * a fortress node) it could never trace build range through, but whatever lies
 * beyond that blocker stays out of reach. Severed edges still block outright,
 * and `freeEdges` still cost nothing to cross.
 */
export function nodesWithinInto(graph, start, maxSteps, blocked, blockedEdges, freeEdges) {
    return reachFrom(graph, [start], maxSteps, blocked, blockedEdges, freeEdges, true);
}
/**
 * Distance in edges between two nodes, or `undefined` if `b` isn't reachable
 * from `a` within `maxSteps` (default: the whole graph). Honours the same
 * `blocked` / `blockedEdges` / `freeEdges` traversal rules as `nodesWithin` — so
 * distance is the real, route-around-obstacles distance the game cares about,
 * not straight-line.
 * For a one-step check prefer `areAdjacent`; for "every node within R" use
 * `nodesWithin` directly (one BFS instead of one per target).
 */
export function nodeDistance(graph, a, b, opts = {}) {
    if (a === b)
        return 0;
    const max = opts.maxSteps ?? graph.nodes.length;
    return nodesWithin(graph, a, max, opts.blocked, opts.blockedEdges, opts.freeEdges).get(b);
}
/**
 * The shortest route from `from` to `to` as the **inclusive** list of node ids
 * to walk `[from, …, to]`, or `undefined` if there's no legal route (honouring
 * the same obstructions as `nodesWithin`). So the trip is `path.length - 1`
 * edges, with `path.length - 2` nodes strictly *between* the endpoints.
 *
 * **Broken paths invalidate the traverse:** an edge in `opts.blockedEdges`
 * (severed paths / chasms) is never stepped across, and a node in `opts.blocked`
 * is never entered. The BFS routes *around* them; if the only way through is
 * over a chasm (or a Bridge would be needed) the result is `undefined`. When
 * several shortest routes exist, one is returned (BFS / adjacency order); ties
 * aren't enumerated.
 *
 * `opts.freeEdges` cost nothing to cross, so "shortest" means fewest PAID edges
 * — the returned route may be longer in nodes than a route that pays for every
 * step.
 */
export function nodePath(graph, from, to, opts = {}) {
    if (!graph.nodes[from] || !graph.nodes[to])
        return undefined;
    if (from === to)
        return [from];
    const { maxSteps = graph.nodes.length, blocked, blockedEdges, freeEdges } = opts;
    // BFS from `from`, recording each node's predecessor so the route can be
    // rebuilt once `to` is reached. `seen` doubles as the visited set.
    const prev = new Map();
    const seen = new Set([from]);
    const routeTo = () => {
        const path = [to];
        for (let cur = to; cur !== from;) {
            cur = prev.get(cur);
            path.push(cur);
        }
        return path.reverse();
    };
    // Zero-cost edges are flooded out of each layer before the next paid step, so
    // a chain of them extends the route without spending from `maxSteps`.
    const flood = (layer) => {
        if (!freeEdges || freeEdges.size === 0)
            return layer;
        const out = layer.slice();
        for (let i = 0; i < out.length; i++) {
            const id = out[i];
            for (const nb of graph.adjacency[id] ?? []) {
                if (seen.has(nb) || blocked?.has(nb))
                    continue;
                if (!freeEdges.has(edgeKey(id, nb)))
                    continue;
                if (blockedEdges?.has(edgeKey(id, nb)))
                    continue; // severed path
                seen.add(nb);
                prev.set(nb, id);
                if (nb === to)
                    return null; // found — `routeTo` reads it off `prev`
                out.push(nb);
            }
        }
        return out;
    };
    let frontier = flood([from]);
    if (frontier === null)
        return routeTo();
    for (let step = 1; step <= maxSteps && frontier.length > 0; step++) {
        const next = [];
        for (const id of frontier) {
            for (const nb of graph.adjacency[id] ?? []) {
                if (seen.has(nb) || blocked?.has(nb))
                    continue;
                if (blockedEdges?.has(edgeKey(id, nb)))
                    continue; // severed path — can't cross
                seen.add(nb);
                prev.set(nb, id);
                if (nb === to)
                    return routeTo();
                next.push(nb);
            }
        }
        const flooded = flood(next);
        if (flooded === null)
            return routeTo();
        frontier = flooded;
    }
    return undefined; // unreachable within maxSteps (e.g. cut off by chasms)
}
//# sourceMappingURL=nodes.js.map